import classnames from 'classnames';
import { forwardRef } from 'react';
import type { ReactNode, Ref } from 'react';

import CharacterCount from './CharacterCount';
import InputBase from './InputBase';
import type { InputBaseProps, InputRenderProps } from './InputBase';

export type TextInputVariant = 'default' | 'underlined';

//  TODO: this is nuts, lets do an omit instead...
export type TextInputProps =
  Pick<InputBaseProps,
  | 'autoFocus'
  | 'id'
  | 'isDisabled'
  | 'isCopyable'
  | 'isReadOnly'
  | 'isRequired'
  | 'label'
  | 'maxLength'
  | 'onChange'
  | 'onKeyDown'
  | 'placeholder'
  | 'size'
  | 'testId'
  | 'title'
  | 'type'
  | 'value'
  | 'name'
  | 'onBlur'
  | 'onFocus'
  > & {
    /**
     * If the char count should be displayed.
     */
    showCharCount?: boolean;
    /**
     * element to put at the end of the text input
     */
    suffix?: ReactNode | string;
    /**
     * Text input values should always be strings
     */
    value?: string;
    /**
     * Optional validation regexp for value
     */
    pattern?: RegExp;
    /**
     * Optional: Toggle browser spellchecking of input
     */
    spellCheck?: boolean;
    /**
     * Visual variant. Defaults to `'default'` (full border with rounded corners).
     * `'underlined'` removes the border and rounding, leaving only a bottom rule —
     * suited for inline contexts like the "Other: ___" survey pattern.
     */
    variant?: TextInputVariant;
  };

/**
 * A generic, controlled text input for FOSSA Form elements.
 */
const TextInput = forwardRef<HTMLInputElement, TextInputProps>(({
  autoFocus,
  isDisabled,
  isRequired,
  isCopyable,
  isReadOnly,
  label,
  maxLength,
  onChange,
  onKeyDown,
  placeholder,
  showCharCount,
  size = 'medium',
  suffix,
  testId,
  title,
  type = 'text',
  value,
  pattern,
  spellCheck,
  name,
  onBlur,
  onFocus,
  variant = 'default',
}: TextInputProps, ref: Ref<HTMLInputElement>) => (
  <InputBase
    autoFocus={autoFocus}
    isDisabled={isDisabled}
    isRequired={isRequired}
    isCopyable={isCopyable}
    isReadOnly={isReadOnly}
    label={label}
    maxLength={maxLength}
    onChange={onChange}
    onKeyDown={onKeyDown}
    placeholder={placeholder}
    size={size}
    testId={testId}
    title={title}
    type={type}
    value={value}
    name={name}
    onBlur={onBlur}
    onFocus={onFocus}
  >
    {(inputBaseProps: InputRenderProps) => {
      const inputClassName = classnames(inputBaseProps.className, {
        [`fossa-form__text-input--variant-${variant}`]: variant !== 'default',
      });
      const inputToDisplay = suffix
        ? (
          <div className="fossa-form__text-input__wrapper">
            {/* Suffix placed first so we can target the input with a sibling selector */}
            <div className="fossa-form__text-input__wrapper__suffix">
              {suffix}
            </div>
            <input
              ref={ref}
              {...inputBaseProps}
              className={inputClassName}
            />
          </div>
          ) : (
            <input
              pattern={pattern?.source}
              ref={ref}
              spellCheck={spellCheck}
              {...inputBaseProps}
              className={inputClassName}
          />
          );

      return (maxLength && showCharCount)
        ? (
          <>
            {inputToDisplay}
            <CharacterCount
              maxLength={maxLength}
              currentLength={value?.length ?? 0}
            />
          </>
          ) : inputToDisplay;
    }}
  </InputBase>
));

export default TextInput;
